/**
 * Reading a trip's stored route geometry.
 *
 * `plannedRoute` and `actualRoute` arrive as `unknown` — they are whatever the
 * routing provider returned, stored verbatim. Both functions here are pure so
 * the geometry can be tested without a Maps runtime, and so a malformed or
 * absent route is a `null` rather than a thrown error inside a render.
 *
 * The rule they exist to enforce: no geometry means draw NOTHING. Joining
 * stops with straight lines invents a road that does not exist and tells a
 * dispatcher a leg is 4km when it is 11.
 */

export interface LatLngLiteral {
  lat: number;
  lng: number;
}

/**
 * The encoded polyline from a stored provider route, or null.
 *
 * Shape: `{ routes: [{ polyline: { encodedPolyline: string } }] }`. Anything
 * else — a null route, an empty routes array, a route that failed before it
 * produced geometry — yields null.
 */
export function encodedRoutePolyline(route: unknown): string | null {
  if (!route || typeof route !== "object") return null;

  const routes = (route as { routes?: unknown }).routes;
  if (!Array.isArray(routes) || routes.length === 0) return null;

  const first = routes[0];
  if (!first || typeof first !== "object") return null;

  const polyline = (first as { polyline?: unknown }).polyline;
  if (!polyline || typeof polyline !== "object") return null;

  const encoded = (polyline as { encodedPolyline?: unknown }).encodedPolyline;
  return typeof encoded === "string" && encoded.length > 0 ? encoded : null;
}

/**
 * Decode a Google encoded polyline into coordinates.
 *
 * Implemented here rather than through `google.maps.geometry.encoding` so the
 * decode is exercised by the suite: the Maps library is not loaded in tests,
 * and a route line is exactly the kind of thing that must not be discovered to
 * be wrong in front of a dispatcher.
 *
 * Returns an empty array for anything it cannot decode.
 */
export function decodePolyline(encoded: string): LatLngLiteral[] {
  const points: LatLngLiteral[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    const latDelta = readValue();
    if (latDelta === null) return points;
    lat += latDelta;

    const lngDelta = readValue();
    if (lngDelta === null) return points;
    lng += lngDelta;

    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return points;

  /** One zigzag-encoded, chunked value, or null when the run is truncated. */
  function readValue(): number | null {
    let result = 0;
    let shift = 0;
    let chunk: number;

    do {
      if (index >= encoded.length) return null;
      chunk = encoded.charCodeAt(index++) - 63;
      if (chunk < 0) return null;
      result |= (chunk & 0x1f) << shift;
      shift += 5;
    } while (chunk >= 0x20);

    // Low bit set means the original value was negative.
    return result & 1 ? ~(result >> 1) : result >> 1;
  }
}

/** A stop's coordinates, from its own point or its location key. */
export function stopLatLng(stop: {
  mapLocation?: LatLngLiteral | null;
  locationKey?: string | null;
}): LatLngLiteral | null {
  if (
    stop.mapLocation &&
    Number.isFinite(stop.mapLocation.lat) &&
    Number.isFinite(stop.mapLocation.lng)
  ) {
    return { lat: stop.mapLocation.lat, lng: stop.mapLocation.lng };
  }

  const [lat, lng] = (stop.locationKey ?? "").split(",").map(Number);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
}
